/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="../../components/models/meevo.model.custom.ts" />

module Meevo.Components.HostedPage {
    declare let device;

    const CareCreditTimeout = 30000; // milliseconds
    const HttpResponseTimeout = 408;
    const HttpConflict = 409;

    interface HostedPageState {
        isHostedPageActive: boolean;
    }

    export class HostedPageService implements IHostedPageService {
        private hostedPageState = new Map<Meevo.Model.HostedPageIdType, HostedPageState>();
        private static assetsBaseUrl: string = '/assets/hostedPage';
        private static logosUrl: string = 'logos';
        private static noInfoFound: string = 'no_info_found';
        adyenDeferred: ng.IDeferred<any>;
        parameters: any;

        constructor(
            private dataService: Meevo.Components.BaseClasses.IDataService,
            private busyService: Meevo.Components.Busy.IBusyService,
            private modalService: Meevo.Components.Modal.ModalService,
            private $q: ng.IQService,
            private $filter: ng.IFilterService,
            private $timeout: ng.ITimeoutService,
            private $translate: angular.translate.ITranslateService,
        ) {
        }

        private getMerchantProvider(merchantProviderId: number) {
            switch (merchantProviderId) {
                case Meevo.Model.MerchantAccountProviderEnum.Worldpay:
                case Meevo.Model.MerchantAccountProviderEnum.WorldpayGC:
                    return 'vantiv';
                case Meevo.Model.MerchantAccountProviderEnum.CardConnect:
                    return 'cardconnect';
                case Meevo.Model.MerchantAccountProviderEnum.ConnectE:
                    return 'connecte';
                case Meevo.Model.MerchantAccountProviderEnum.CareCredit:
                    return 'carecredit';
                case Meevo.Model.MerchantAccountProviderEnum.Adyen:
                case Meevo.Model.MerchantAccountProviderEnum.AdyenCanada:
                    return 'adyen';
            }

            return null;
        }

        private getCustomLogoName(merchantProviderId: number) {
            switch (merchantProviderId) {
                case Meevo.Model.MerchantAccountProviderEnum.Adyen:
                case Meevo.Model.MerchantAccountProviderEnum.AdyenCanada:
                    return 'meevopay';
            }

            return this.getMerchantProvider(merchantProviderId);
        }

        private useCustomCss(merchantProviderId: number) {
            switch (merchantProviderId) {
                case Meevo.Model.MerchantAccountProviderEnum.Worldpay:
                case Meevo.Model.MerchantAccountProviderEnum.CardConnect:
                    return true;
            }

            return false;
        }

        private useCustomLogo(merchantProviderId: number) {
            switch (merchantProviderId) {
                case Meevo.Model.MerchantAccountProviderEnum.Worldpay:
                case Meevo.Model.MerchantAccountProviderEnum.CardConnect:
                case Meevo.Model.MerchantAccountProviderEnum.CareCredit:
                case Meevo.Model.MerchantAccountProviderEnum.Adyen:
                case Meevo.Model.MerchantAccountProviderEnum.AdyenCanada:
                    return true;
            }

            return false;
        }

        private useReturnUrl(merchantProviderId: number) {
            switch (merchantProviderId) {
                case Meevo.Model.MerchantAccountProviderEnum.Adyen:
                case Meevo.Model.MerchantAccountProviderEnum.AdyenCanada:
                    return true;
            }

            return false;
        }

        getHostedPageActive(hostedPageId: Meevo.Model.HostedPageIdType): boolean {
            return this.hostedPageState.has(hostedPageId)
                ? this.hostedPageState.get(hostedPageId).isHostedPageActive
                : false;
        }

        setHostedPageActive(hostedPageId: Meevo.Model.HostedPageIdType, active: boolean) {
            if (!this.hostedPageState.has(hostedPageId)) {
                this.hostedPageState.set(hostedPageId, { isHostedPageActive: active });
            }
            const hostedPageState = this.hostedPageState.get(hostedPageId);
            hostedPageState.isHostedPageActive = active;
        }

        reset(hostedPageId: Meevo.Model.HostedPageIdType) {
            this.setHostedPageActive(hostedPageId, false);
        }

        process(processRequest: IHostedPageRequest, hostedPageId: Meevo.Model.HostedPageIdType): ng.IPromise<any> {
            this.busyService.start();

            const useCustomCss = this.useCustomCss(processRequest.merchantProviderId);

            this.setHostedPageActive(hostedPageId, true);

            const deferred = this.$q.defer();

            const callWithHostedPageStyles = (hostedPageStyles: string) =>
                this.setup(processRequest, hostedPageId, hostedPageStyles)
                    .then((response) => deferred.resolve(response))
                    .catch((response) => deferred.reject(response));

            const callWithoutHostedPageStyles = () => {
                this.setup(processRequest, hostedPageId, null)
                    .then((response) => deferred.resolve(response))
                    .catch((response) => deferred.reject(response));
            }

            if (useCustomCss) {
                const merchantProvider = this.getMerchantProvider(processRequest.merchantProviderId);

                const customCssUrl = `${HostedPageService.assetsBaseUrl}/hostedpage.${merchantProvider}.css`;

                $.get(customCssUrl).then(
                    (hostedPageStyles: string) => callWithHostedPageStyles(hostedPageStyles),
                    () => callWithoutHostedPageStyles()
                );
            } else {
                callWithoutHostedPageStyles();
            }

            return deferred.promise;
        }

        private getProcessButtonTitle(isCardOnFile: boolean) {
            return this.$translate.instant(isCardOnFile === true ? "LABEL_BUTTON_ADD_CARD" : "LABEL_BUTTON_PROCESS_TRANSACTION");
        }

        private setup(processRequest: IHostedPageRequest, hostedPageId: Meevo.Model.HostedPageIdType, hostedPageStyles: string = null): ng.IPromise<any> {
            const drawerId = processRequest.drawerId || Guid.empty;

            const setupUrl = `api/drawer/${drawerId}/hostedPageSetup`;

            const setupRequest: Meevo.Components.HostedPage.IHostedPageRequest = {
                paymentAccountId: processRequest.paymentAccountId,
                merchantAccountId: processRequest.merchantAccountId,
                isCardOnFile: processRequest.isCardOnFile,
                clientId: processRequest.clientId,
                externalPaymentAccountId: processRequest.externalPaymentAccountId,
                cardHolderName: processRequest.cardHolderName,
                billingPostalCode: processRequest.billingPostalCode,
                billingPostalCodeRequired: processRequest.billingPostalCodeRequired,
                transactionAmount: processRequest.transactionAmount,
                taxAmount: processRequest.taxAmount,
                customCss: hostedPageStyles,
                isMobile: !device.desktop(),
                sale: processRequest.sale,
                paymentLineTempId: processRequest.paymentLineTempId,
                adminPage: processRequest.adminPage,
                processButtonTitle: this.getProcessButtonTitle(processRequest.isCardOnFile),
                allowPartialApprovals: processRequest.allowPartialApprovals,
                returnUrl: null,
                isHsaFsa: processRequest.isHsaFsa,
                hostedPageIntent: processRequest.hostedPageIntent,
                isSecurityCode: processRequest.isSecurityCode,
                hasMeevoPaySplitFunding: processRequest.hasMeevoPaySplitFunding,
            };

            const deferred = this.$q.defer();

            this.setHostedPageActive(hostedPageId, true);

            const iframe = $(`#${hostedPageId} iframe`);

            if (this.useCustomLogo(processRequest.merchantProviderId)) {
                const logo = $(`#${hostedPageId} .hosted-page-logo`);

                if (logo.attr('src') == '') {
                    const customLogoName = this.getCustomLogoName(processRequest.merchantProviderId);
                    logo.attr('src', `${HostedPageService.assetsBaseUrl}/${HostedPageService.logosUrl}/${customLogoName}.png`)
                }
            }

            if (this.useReturnUrl(processRequest.merchantProviderId)) {
                setupRequest.returnUrl = this.getCustomHtmlBaseUrl(processRequest.merchantProviderId);
            }

            const finish = (processRequest) => {
                this.busyService.stop();
                if (!processRequest.isSecurityCode) {
                    this.reset(hostedPageId);
                    iframe.prop('src', '');
                }
            }

            this.busyService.start();

            this.dataService.customMethodData(setupUrl, "POST", setupRequest)
                .then((setupResponse: any) => {
                    if (setupResponse.status == HttpConflict || !setupResponse.data) {
                        finish(processRequest);

                        console.error('setupResponse error (then)', setupResponse);

                        return deferred.reject(setupResponse);
                    }

                    const processResponse = setupResponse.data;

                    const setupId = processResponse.transactionSetupId;
                    const paymentRequestId = processResponse.paymentRequestId;
                    const onHoldId = processResponse.onHoldId;

                    if (processRequest.tokenCreatedCallback) {
                        processRequest.tokenCreatedCallback.call(this, setupId, paymentRequestId, onHoldId);
                    }

                    if (processRequest.paymentAccountTokenCreatedCallback) {
                        processRequest.paymentAccountTokenCreatedCallback(setupResponse.data.paymentAccountReferenceNumber)
                    }

                    let providerHandler = null;

                    switch (processRequest.merchantProviderId) {
                        case Meevo.Model.MerchantAccountProviderEnum.Worldpay:
                            providerHandler = this.worldpayHandler;
                            break;

                        case Meevo.Model.MerchantAccountProviderEnum.CardConnect:
                            providerHandler = this.cardConnectHandler;
                            break;

                        case Meevo.Model.MerchantAccountProviderEnum.ConnectE:
                            providerHandler = this.connectEHandler;
                            break;

                        case Meevo.Model.MerchantAccountProviderEnum.CareCredit:
                            providerHandler = this.careCreditHandler;
                            break;

                        case Meevo.Model.MerchantAccountProviderEnum.Adyen:
                        case Meevo.Model.MerchantAccountProviderEnum.AdyenCanada:
                            providerHandler = this.adyenHandler;
                            break;

                        default:
                            finish(processRequest);

                            const message = `unrecognized merchant provider id: ${processRequest.merchantProviderId}`;
                            console.error(message);

                            return deferred.reject();
                    }

                    this.busyService.stop();

                    return providerHandler.call(this, processRequest, processResponse, iframe, hostedPageId)
                        .then((response) => deferred.resolve(response))
                        .catch((response) => deferred.reject(response))
                        .finally(() => {
                            finish(processRequest);
                        });
                })
                .catch((hostedPageSetupResponse: any) => {
                    finish(processRequest);

                    console.error('setupResponse error (catch)', hostedPageSetupResponse);

                    return deferred.reject(hostedPageSetupResponse);
                });

            return deferred.promise;
        }

        private getIframeWindow(iframe: any) {
            const iframeWindow = (<HTMLIFrameElement>iframe[0]).contentWindow || iframe[0];
            return iframeWindow;
        }

        private worldpayHandler(processRequest: IHostedPageRequest, processResponse: IHostedPageResponse, iframe: any, hostedPageId: Meevo.Model.HostedPageIdType): ng.IPromise<any> {
            const deferred = this.$q.defer();

            const setupId = processResponse.transactionSetupId;

            iframe
                .off('load')
                .on('load', () => {
                    if (!setupId) {
                        console.error('Worldpay processResponse.transactionSetupId is missing!');
                        deferred.reject();
                        return;
                    }
                    //Prevent unnecessary  api call that happens when iframe src set to blank
                    if (!this.getHostedPageActive(hostedPageId)) {
                        return;
                    }
                    console.info('HostedPage worldpayHandler', 'Update', setupId);

                    this.update(processRequest, processResponse, setupId)
                        .then((updateResponse: any) => {
                            updateResponse.transactionSetupId = setupId;
                            deferred.resolve(updateResponse)
                        })
                        .catch((updateResponse) => updateResponse === HostedPageService.noInfoFound || deferred.reject(updateResponse));
                })
                .attr('src', processResponse.hostedPageUrl);

            return deferred.promise;
        }

        private cardConnectHandler(processRequest: IHostedPageRequest, processResponse: IHostedPageResponse, iframe: any, hostedPageId: Meevo.Model.HostedPageIdType): ng.IPromise<any> {
            const deferred = this.$q.defer();

            let customHtmlUrl = this.getCustomHtmlUrl(processRequest);

            const SubmitSource = 'HostedPageCardConnectSubmit';

            const eventHandler = (event: any) => {
                if (!angular.isObject(event.data)) {
                    return;
                }

                if (event.data.source !== SubmitSource) {
                    return;
                }

                console.log(SubmitSource, event.data);

                const token = event.data.token;
                const expirationMonth = event.data.expirationMonth;
                const expirationYear = event.data.expirationYear;

                console.info('HostedPage cardConnectHandler', 'Update', token);

                this.update(processRequest, processResponse, token, expirationMonth, expirationYear)
                    .then((updateResponse) => deferred.resolve(updateResponse))
                    .catch((updateResponse) => updateResponse === HostedPageService.noInfoFound || deferred.reject(updateResponse));
            };

            iframe
                .off('load')
                .on('load', () => {
                    iframe.contents().find("#HostedPageCardConnect").attr('src', processResponse.hostedPageUrl);

                    const iframeWindow = this.getIframeWindow(iframe);

                    iframeWindow.removeEventListener('message', eventHandler, false);
                    iframeWindow.addEventListener('message', eventHandler, false);
                })
                .attr('src', customHtmlUrl);

            return deferred.promise;
        }

        private connectEHandler(processRequest: IHostedPageRequest, processResponse: IHostedPageResponse, iframe: any, hostedPageId: Meevo.Model.HostedPageIdType): ng.IPromise<any> {
            const deferred = this.$q.defer();

            const setupId = processResponse.transactionSetupId;

            let customHtmlUrl = this.getCustomHtmlUrl(processRequest, processResponse);

            const SubmitSource = 'HostedPagePaymentSenseSubmit';

            const eventHandler = (event: any) => {
                if (!angular.isObject(event.data)) {
                    return;
                }

                if (event.data.source !== SubmitSource) {
                    return;
                }

                console.log(SubmitSource, event.data);

                if (event.data.failed) {
                    if (angular.isArray(event.data.response)) {
                        // validation errors
                        return;
                    }

                    console.error(event.data.response);

                    deferred.reject(event.data.response);
                    return;
                }

                console.info(SubmitSource, 'Update', setupId);

                this.update(processRequest, processResponse, setupId)
                    .then((updateResponse) => deferred.resolve(updateResponse))
                    .catch((updateResponse) => updateResponse === HostedPageService.noInfoFound || deferred.reject(updateResponse));
            };

            iframe
                .off('load')
                .on('load', () => {
                    if (!setupId) {
                        console.error('Connect-E processResponse.transactionSetupId is missing!');
                        deferred.reject();
                        return;
                    }

                    const iframeWindow = this.getIframeWindow(iframe);

                    iframeWindow.removeEventListener('message', eventHandler, false);
                    iframeWindow.addEventListener('message', eventHandler, false);
                })
                .attr('src', customHtmlUrl);

            return deferred.promise;
        }

        private careCreditHandler(processRequest: IHostedPageRequest, processResponse: IHostedPageResponse, iframe: any, hostedPageId: Meevo.Model.HostedPageIdType): ng.IPromise<any> {
            const deferred = this.$q.defer();

            const setupId = processResponse.transactionSetupId;

            let customHtmlUrl = this.getCustomHtmlUrl(processRequest, processResponse);

            const SubmitSource = 'HostedPageCareCreditSubmit';

            const eventHandler = (event: any) => {
                if (!angular.isObject(event.data)) {
                    return;
                }

                if (event.data.source !== SubmitSource) {
                    return;
                }

                console.log(SubmitSource, event.data);

                const referenceId = event.data.referenceId;

                console.info(SubmitSource, 'Update', referenceId);

                this.update(processRequest, processResponse, referenceId)
                    .then((updateResponse) => deferred.resolve(updateResponse))
                    .catch((updateResponse) => {
                        if (updateResponse === HostedPageService.noInfoFound) {
                            // CareCredit will handle 'no info found' as a valid response
                            deferred.resolve(updateResponse);
                        }
                        else {
                            deferred.reject(updateResponse);
                        }
                    });
            };

            let timeoutPromise;

            if (CareCreditTimeout > 0) {
                console.debug(`CareCredit hosted page timeout started: ${CareCreditTimeout} milliseconds`);
                timeoutPromise = this.getTimeoutHandler(iframe, CareCreditTimeout, hostedPageId, () => {
                    console.error(`CareCredit hosted page timed out.`);
                    // timeout
                    deferred.reject({ status: HttpResponseTimeout });
                });
            }

            iframe
                .off('load')
                .on('load', () => {
                    if (timeoutPromise) {
                        this.$timeout.cancel(timeoutPromise);
                        console.debug(`CareCredit hosted page timeout cancelled.`);
                    }

                    if (!setupId) {
                        console.error('CareCredit processResponse.transactionSetupId is missing!');
                        deferred.reject();
                        return;
                    }

                    const iframeWindow = this.getIframeWindow(iframe);

                    iframeWindow.removeEventListener('message', eventHandler, false);
                    iframeWindow.addEventListener('message', eventHandler, false);
                })
                .attr('src', customHtmlUrl);

            return deferred.promise;
        }

        setSecurityCodeDeferred(securityCodeDeferred) {
            this.adyenDeferred = securityCodeDeferred;
        }

        reloadSecurityCode(hostedPageId: string, cardType: string) {
            this.parameters.cardType = cardType;

            const iframe = $(`#${hostedPageId} iframe`);
            const iframeWindow = this.getIframeWindow(iframe);
            var message = {
                source: "HostedPageAdyenReloadSecurityCode",
                parameters: this.parameters
            };
            iframeWindow.postMessage(message, '*');
        }

        collectSecurityCode(hostedPageId: string) {
            const iframe = $(`#${hostedPageId} iframe`);
            const iframeWindow = this.getIframeWindow(iframe);
            var message = {
                source: "HostedPageAdyenCollectSecurityCode"
            };
            iframeWindow.postMessage(message, '*');
        }

        private adyenHandler(processRequest: IHostedPageRequest, processResponse: IHostedPageResponse, iframe: any, hostedPageId: Meevo.Model.HostedPageIdType): ng.IPromise<any> {
            this.adyenDeferred = this.$q.defer();

            const setupId = processResponse.transactionSetupId;

            const parameters = this.getParameters(processRequest, processResponse);
            const customHtmlUrl = this.getCustomHtmlBaseUrl(processRequest.merchantProviderId);

            const SubmitSource = 'HostedPageAdyenSubmit';
            const InitSource = 'HostedPageAdyenInit';
            const RedirectSource = 'HostedPageAdyenRedirect';
            const MeevoAlert = 'HostedPageMeevoAlert';

            const checkRedirect = (updateResponse: any) => {
                if (!updateResponse.isRedirect) {
                    return false;
                }

                const redirectPayload = JSON.parse(updateResponse.processorResponseDetail);

                const iframeWindow = this.getIframeWindow(iframe);
                var message = {
                    source: RedirectSource,
                    redirectPayload: redirectPayload,
                };

                iframeWindow.postMessage(message, '*');

                return true;
            };

            const submitEventHandler = (event: any) => {
                console.log(event.data.source, event.data);

                if (event.data.failed) {
                    console.error(event.data.response);

                    this.adyenDeferred.reject(event.data.response);
                    return;
                }

                console.info(SubmitSource, 'Update', setupId);

                if (event.data.isSecurityCode) {
                    this.adyenDeferred.resolve(event.data.response);
                    return;
                }

                if (!event.data.isRedirect) {
                    if (this.useReturnUrl(processRequest.merchantProviderId)) {
                        event.data.response.returnUrl = this.getCustomHtmlBaseUrl(processRequest.merchantProviderId);
                    }
                }

                const setupData = btoa(JSON.stringify(event.data.response));

                this.update(processRequest, processResponse, setupId, null, null, setupData)
                    .then((updateResponse: any) => {
                        if (!checkRedirect(updateResponse)) {
                            return this.adyenDeferred.resolve(updateResponse);
                        }
                    })
                    .catch((updateResponse) => this.adyenDeferred.reject(updateResponse));
            };

            const meevoAlertEventHandler = (event: any) => {
                console.log(event.data.source, event.data);

                this.modalService.alert(event.data.response, Modal.ModalTranslationModes.Translate, true);
            };

            const eventHandler = (event: any) => {
                if (!angular.isObject(event.data)) {
                    return;
                }

                switch (event.data.source) {
                    case SubmitSource:
                        submitEventHandler(event);
                        break;

                    case MeevoAlert:
                        meevoAlertEventHandler(event);
                        break;
                }
            };

            iframe
                .off('load')
                .on('load', (event) => {
                    /*
                    if (!setupId) {
                        console.error('Adyen processResponse.transactionSetupId is missing!');
                        deferred.reject();
                        return;
                    }
                    */
                    console.log('onload', event);

                    try {
                        const iframeWindow = this.getIframeWindow(iframe);

                        iframeWindow.removeEventListener('message', eventHandler, false);
                        iframeWindow.addEventListener('message', eventHandler, false);
                        this.parameters = parameters;

                        var message = {
                            source: InitSource,
                            parameters: parameters,
                        };

                        iframeWindow.postMessage(message, '*');
                    }
                    catch (ex) {
                        // a cross-origin page (redirect site)
                    }
                })
                .attr('src', customHtmlUrl);

            return this.adyenDeferred.promise;
        }

        private getTimeoutHandler(iframe, timeoutMs: number, hostedPageId: Meevo.Model.HostedPageIdType, callback: Function = undefined) {
            return this.$timeout(() => {
                this.reset(hostedPageId);

                iframe
                    .off('load')
                    .attr('src', 'about:blank')
                    .empty();

                if (callback) {
                    callback();
                }
            }, timeoutMs);
        }

        private getCustomHtmlBaseUrl(merchantProviderId: number): string {
            const merchantProvider = this.getMerchantProvider(merchantProviderId);

            const customerHtmlBaseUrl = `${window.location.origin}${HostedPageService.assetsBaseUrl}/hostedpage.${merchantProvider}.html`;

            return customerHtmlBaseUrl;
        }

        private getParameters(processRequest: IHostedPageRequest, processResponse: IHostedPageResponse = null) {
            const map = {};

            if (processRequest.transactionAmount) {
                let meevoCurrency = this.$filter('meevoCurrency') as any;
                let transactionAmount = meevoCurrency(processRequest.transactionAmount);
                map['transactionAmount'] = transactionAmount;
            }

            map['btnaction'] = this.getProcessButtonTitle(processRequest.isCardOnFile);
            map['merchantProviderId'] = processRequest.merchantProviderId;

            if (processRequest.isSecurityCode) {
                map['securityCode'] = processRequest.isSecurityCode;
                map['externalPaymentAccountId'] = processRequest.externalPaymentAccountId;
                map['cardType'] = processRequest.cardType;
            }

            if (processResponse) {
                if (processResponse.processorTransactionAmount) {
                    map['processorAmount'] = processResponse.processorTransactionAmount;
                }

                if (processResponse.setupId) {
                    map['setupId'] = processResponse.setupId;
                }

                if (processResponse.hostedPageUrl) {
                    map['hostedPageUrl'] = processResponse.hostedPageUrl;
                }

                if (processResponse.hostedPageToken) {
                    map['hostedPageToken'] = processResponse.hostedPageToken;
                }

                if (processResponse.hostedPageData) {
                    map['hostedPageData'] = processResponse.hostedPageData;
                }

                if (processResponse.billingPostalCodeRequired) {
                    map['billingPostalCodeRequired'] = processResponse.billingPostalCodeRequired;
                }

                if (processResponse.billingPostalCode) {
                    map['billingPostalCode'] = processResponse.billingPostalCode;
                }

                if (processResponse.billingCountryCode) {
                    map['billingCountryCode'] = processResponse.billingCountryCode;
                }

                if (processResponse.cardholderName) {
                    map['cardholderName'] = processResponse.cardholderName;
                }
            }

            return map;
        }

        private getCustomHtmlUrl(processRequest: IHostedPageRequest, processResponse: IHostedPageResponse = null): string {
            let map = this.getParameters(processRequest, processResponse);
            let parameters = [];

            for (const item in map)
            {
                parameters.push(item + "=" + encodeURIComponent(map[item]));
            }

            let customHtmlUrl = this.getCustomHtmlBaseUrl(processRequest.merchantProviderId);

            if (parameters.length > 0) {
                let queryString = '?' + parameters.join('&');
                customHtmlUrl += queryString;
            }

            return customHtmlUrl;
        }

        private update(
            processRequest: IHostedPageRequest,
            processResponse: IHostedPageResponse,
            setupId: string,
            expirationMonth: string = null,
            expirationYear: string = null,
            setupData: string = null
        ) {
            const drawerId = processRequest.drawerId || Guid.empty;

            const updateUrl = `api/drawer/${drawerId}/hostedPageUpdate/${setupId}`;

            const updateRequest = {
                merchantAccountId: processRequest.merchantAccountId,
                setupId: setupId,
                isCardOnFile: processRequest.isCardOnFile,
                paymentAccountReferenceNumber: processResponse.paymentAccountReferenceNumber,
                expirationMonth: expirationMonth,
                expirationYear: expirationYear,
                cardHolderName: processRequest.cardHolderName,
                postalCode: processRequest.billingPostalCode,
                transactionAmount: processRequest.transactionAmount,
                taxAmount: processRequest.taxAmount,
                paymentRequestId: processResponse.paymentRequestId,
                onHoldId: processResponse.onHoldId,
                paymentAccountRequestId: processResponse.paymentAccountRequestId,
                setupData: setupData,
                isHsaFsa: processRequest.isHsaFsa,
            };

            const deferred = this.$q.defer();

            this.busyService.start();

            this.dataService.customMethodData(updateUrl, "POST", updateRequest)
                .then((updateResponse: any) => {
                    this.busyService.stop();

                    if (updateResponse.status == HttpConflict) {
                        deferred.reject(updateResponse);
                        return;
                    }

                    if (updateResponse.data.noRecordsFound === false) {
                        deferred.resolve(updateResponse.data);
                        return;
                    }

                    deferred.reject(HostedPageService.noInfoFound);
                })
                .catch((updateResponse: any) => {
                    this.busyService.stop();

                    deferred.reject(updateResponse);
                });

            return deferred.promise;
        }

        static factory(): Function {
            let svc = (
                dataService: Meevo.Components.BaseClasses.IDataService,
                busyService: Meevo.Components.Busy.IBusyService,
                modalService: Meevo.Components.Modal.ModalService,
                $q: angular.IQService,
                $filter: ng.IFilterService,
                $timeout: ng.ITimeoutService,
                $translate: angular.translate.ITranslateService
            ) => new HostedPageService(
                dataService,
                busyService,
                modalService,
                $q,
                $filter,
                $timeout,
                $translate
            );

            svc.$inject = [
                "meevo.components.common.DataService",
                "meevo.components.busy.BusyService",
                "meevo.components.modal.ModalService",
                "$q",
                "$filter",
                "$timeout",
                "$translate",
            ];

            return svc;
        }
    }

    angular.module("meevo.components.hostedPage")
        .factory("meevo.components.hostedPage.HostedPageService", HostedPageService.factory());
}